import AppContext from "@/AppContext";
import LucidMascot from "@/assets/images/lucid-mascot.png";
import contentBenefits from "@/assets/json/content-benefits.json";
import startupBenefits from "@/assets/json/startup-benefits.json";
import { hidePurchasePopup, purchasePopup, userAtom } from "@/Atoms";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import availablePricesDev from "@/hooks/availablePrices.dev";
import availablePricesMonthlyDev from "@/hooks/availablePrices.monthly.dev";
import availablePricesMonthlyProd from "@/hooks/availablePrices.monthly.prod";
import availablePricesProd from "@/hooks/availablePrices.prod";
import { getStripePrice } from "@/services/api/Stripe";
import { createAbandonedCart } from "@/services/api/User";
import { ArrowFatLineUp, Check, X } from "@phosphor-icons/react";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import axios from "axios";
import { useAtom } from "jotai";
import Image from "next/legacy/image";
import { useRouter } from "next/router";
import { useContext, useEffect, useState } from "react";
import CardForm from "../stripe/CardForm";
import { ScrollArea } from "../ui/scroll-area";

const stripePromise = loadStripe(
  process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY
);

const steps = [
  { id: "Step 1", name: "Startup Information", href: "#", status: "current" },
  { id: "Step 2", name: "Payment Details", href: "#", status: "" },
];

const Purchase = () => {
  const [openPurchasePopup, setOpenPurchasePopup] = useAtom(purchasePopup);
  const [isHidePurchasePopup, setIsHidePurchasePopup] =
    useAtom(hidePurchasePopup);

  const router = useRouter();
  const { type } = router.query;
  const planType =
    openPurchasePopup === "startup" ? "Startup Registration" : "Content Access";
  const context = useContext(AppContext);
  const { planSubscription, loading, refreshUserSubscription } = context.state;
  const [playing, setIsPlaying] = useState(false);
  const [state, setState] = useState(null);
  const [userState, setUserState] = useAtom(userAtom);

  const options = {
    // passing the client secret obtained from the server
    clientSecret: process.env.STRIPE_SECRET_KEY,
  };

  const handleUserIpAddress = async () => {
    const availablePrices =
      process.env.NEXT_PUBLIC_ENV !== "production"
        ? availablePricesDev
        : availablePricesProd;

    const availablePricesMonthly =
      process.env.NEXT_PUBLIC_ENV !== "production"
        ? availablePricesMonthlyDev
        : availablePricesMonthlyProd;

    let priceData = {};
    let priceDataMonthly = {};
    let currencyCode = "USD";
    let priceIds = availablePrices["USD"];
    let priceIdsMonthly = availablePricesMonthly["USD"];
    const codes = ["AED", "IDR", "JOD", "EGP", "SAR"];
    let response = null;

    // Set default state first to avoid loading delays
    const setDefaultState = async () => {
      try {
        for (const pKey in priceIds) {
          const stripePriceDataResponse = await getStripePrice(priceIds[pKey]);
          const stripePriceData = stripePriceDataResponse.data;

          const stripePriceDataMonthlyResponse = await getStripePrice(
            priceIdsMonthly[pKey]
          );
          const stripePriceDataMonthly = stripePriceDataMonthlyResponse.data;

          priceData[pKey] = stripePriceData;
          priceDataMonthly[pKey] = stripePriceDataMonthly;
        }

        setState({
            userLocation: null,
            currencyCode,
            priceData,
            priceDataMonthly,
            priceIds,
            priceIdsMonthly,
            ip: null,
          });
      } catch (error) {
        console.error("Error fetching price data:", error);
      }
    };

    // Set default state immediately
    await setDefaultState();

    // Try to get geolocation data with timeout
    try {
      const { getClientGeolocationData } = await import("@/utils/geolocation");
      response = await getClientGeolocationData();

      if (response?.success && response?.currency_code) {
        const currencyCodeFromApi = response.currency_code;
        if (codes.includes(currencyCodeFromApi)) {
          currencyCode = currencyCodeFromApi;
          priceIds = availablePrices[currencyCode];
          priceIdsMonthly = availablePricesMonthly[currencyCode];

          // Update state with new currency
          priceData = {};
          priceDataMonthly = {};

          for (const pKey in priceIds) {
            const stripePriceDataResponse = await getStripePrice(priceIds[pKey]);
            const stripePriceData = stripePriceDataResponse.data;

            const stripePriceDataMonthlyResponse = await getStripePrice(
              priceIdsMonthly[pKey]
            );
            const stripePriceDataMonthly = stripePriceDataMonthlyResponse.data;

            priceData[pKey] = stripePriceData;
            priceDataMonthly[pKey] = stripePriceDataMonthly;
          }

          setState({
            userLocation: response,
            currencyCode,
            priceData,
            priceDataMonthly,
            priceIds,
            priceIdsMonthly,
            ip: response.ip,
          });
        }
      }
    } catch (error) {
      console.error("Geolocation API failed or timed out, using default USD pricing:", error);
      // State is already set with USD defaults, so we can continue
    }
  };

  const handleAbandonedCart = async () => {
    const res = await createAbandonedCart({
      user_id: userState?.id,
      email: userState?.email,
      action: "Click Upgrade Plan Button",
      location: router.pathname,
      label:
        openPurchasePopup === "startup"
          ? "Startup Registration"
          : "Researcher All-Access Pass",
    });
  };

  useEffect(() => {
    if (openPurchasePopup) {
      handleUserIpAddress();
      handleAbandonedCart();
    }
  }, [openPurchasePopup]);

  return (
    <Dialog open={!!openPurchasePopup} onOpenChange={setOpenPurchasePopup}>
      <DialogContent
        isHidePurchasePopup={isHidePurchasePopup}
        className={`max-w-6xl p-0 bg-white text-background ${isHidePurchasePopup ? "hidden" : ""
          }`}
        hideCloseButton
      >
        <ScrollArea className='max-h-[100dvh]'>
          <div className='flex flex-col lg:flex-row'>
            <div className='relative lg:flex-1 lg:m-2.5 lg:mr-0 overflow-hidden bg-gray-100 lg:rounded-md shadow-inner'>
              <button
                onClick={() => setOpenPurchasePopup(false)}
                type='button'
                className='absolute inline-flex md:hidden items-center justify-center rounded-full w-9 h-9 bg-white/[0.15] right-3 top-3'
              >
                <X className='text-xl text-white' />
              </button>
              <div className='p-8 pt-5 text-center text-white lg:pb-10 bg-gradient-to-tr from-orange-1 to-red-1'>
                <span className='inline-flex items-center justify-center mb-3 text-3xl rounded-full w-14 h-14 bg-white/20'>
                  <ArrowFatLineUp />
                </span>
                <p className='text-3xl font-semibold tracking-tight lg:text-4xl font-display'>
                  {openPurchasePopup === "startup"
                    ? "Start Your 7-Day Free Trial of the Startup Registration Plan Today"
                    : "Upgrade to Researcher All-Access Plan"}
                  !
                </p>
                <p className='mt-3 opacity-80'>
                  {openPurchasePopup === "startup"
                    ? "We offer a limited 7-day free trial to startups to create and publish your own startup profile, and start browsing other startups and investors on our database."
                    : "and enjoy all of our content without limitation"}
                </p>
              </div>
              <div className='justify-center hidden lg:flex lg:-mt-5'>
                <div className='flex items-center p-1 px-3 bg-white rounded-full shadow-lg gap-x-2'>
                  <div className='overflow-hidden rounded-full w-9 h-9'>
                    <div className='relative h-14 mx-auto aspect-[199/320]'>
                      <Image
                        src={LucidMascot.src}
                        alt=''
                        layout={"fill"}
                        objectFit={"cover"}
                      />
                    </div>
                    {/* <div className='relative w-9 h-9 aspect-square'>
                      <Image
                        src={allAccessIcon.src}
                        alt=''
                        layout='fill'
                        objectFit='cover'
                      />
                    </div> */}
                  </div>
                  <div>
                    <p className='text-xs font-medium leading-none'>
                      {openPurchasePopup === "startup"
                        ? "Startup Registration"
                        : "Researcher All-Access Pass"}
                    </p>
                  </div>
                </div>
              </div>
              <div className='grid gap-2 p-5 text-sm lg:grid-cols-2 lg:text-xs lg:p-8 text-background'>
                {(openPurchasePopup === "startup"
                  ? startupBenefits
                  : contentBenefits?.benefits
                ).map((item, index) => (
                  <div
                    className='inline-flex items-start space-x-2.5'
                    key={index}
                  >
                    <span className='inline-flex items-center justify-center flex-shrink-0 w-[18px] h-[18px] text-white rounded-full bg-gradient-to-tr from-green-300 to-green-500'>
                      <Check weight='bold' className='text-xs' />
                    </span>
                    <span className='translate-y-px'>{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className='p-5 lg:p-7 lg:w-2/5'>
              {state ? (
                <Elements stripe={stripePromise} options={options}>
                  <CardForm
                    theme='light'
                    planType={
                      openPurchasePopup === "startup"
                        ? "Startup Registration"
                        : "Content Access"
                    }
                    refreshUserSubscription={refreshUserSubscription}
                    planSubscription={planSubscription}
                    {...{
                      userLocation: state?.userLocation,
                      priceData: state?.priceData,
                      priceDataMonthly: state?.priceDataMonthly,
                      priceIds: state?.priceIds,
                      priceIdsMonthly: state?.priceIdsMonthly,
                      currencyCode: state?.currencyCode,
                      ip: state?.ip,
                    }}
                  />
                </Elements>
              ) : (
                <div className='flex items-center justify-center'>
                  <p className='text-slate-500 animate-pulse'>
                    Loading Payment
                  </p>
                </div>
              )}
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default Purchase;
